/* CONTEXT:START
Wave: 3
Status: implemented
Dependencies: [API Gateway, Cognito authentication, Lambda functions]
Risk-Level: medium
Validation-Method: integration-test
Performance-Target: <2s
Last-Updated: 2025-08-05
CONTEXT:END */

import { 
  PenholderAssignment, 
  PenholderMetrics, 
  Submission, 
  WorkflowState,
  SectionedSubmissions,
  AssignmentsResponse
} from '../types';

/**
 * Real API service for penholder operations
 * Replaces MockPenholderService with actual Lambda function calls
 */
export class PenholderApiService {
  private baseUrl: string;
  private authToken: string | null = null;

  constructor() {
    // Use environment variable or fallback to correct API Gateway URL
    this.baseUrl = process.env.REACT_APP_API_URL || 'https://x2n6b1kv66.execute-api.us-east-1.amazonaws.com/dev';
  }

  /**
   * Set authentication token from Cognito
   */
  setAuthToken(token: string) {
    this.authToken = token;
  }

  /**
   * Get current penholder assignment
   */
  async getCurrentAssignment(): Promise<PenholderAssignment | null> {
    try {
      console.log('🔍 PenholderApiService: Getting current assignment...');
      console.log('🔗 API Base URL:', this.baseUrl);
      console.log('🔑 Auth token available:', !!this.authToken);
      
      const response = await this.makeRequest('/api/penholder/assignments/current', 'GET');
      console.log('📡 API Response:', response);
      
      if (response.success && response.data) {
        const assignment = {
          id: response.data.id || response.data.assignmentId,
          penholderUserId: response.data.userId,
          penholderUserName: response.data.userName, // Add userName field
          month: response.data.month,
          year: response.data.year,
          assignedAt: response.data.assignedAt,
          isActive: response.data.status === 'active' || response.data.active
        };
        console.log('✅ Parsed assignment:', assignment);
        return assignment;
      }
      
      console.log('❌ No assignment data in response');
      return null;
    } catch (error) {
      console.error('❌ Error getting current assignment:', error);
      
      // Handle 404 (API Gateway endpoint not configured) by falling back to direct Lambda test
      if (error instanceof Error && (error.message.includes('404') || error.message.includes('403'))) {
        console.warn('⚠️ API Gateway penholder endpoints not configured. Using fallback logic.');
        return this.getFallbackAssignment();
      }
      
      throw new Error('Failed to get current penholder assignment');
    }
  }

  /**
   * Get submissions assigned to current penholder
   */
  async getAssignedSubmissions(penholderUserId?: string): Promise<Submission[]> {
    try {
      const response = await this.makeRequest('/api/penholder/submissions', 'GET');
      
      if (response.success && response.data && response.data.submissions) {
        return response.data.submissions.map((submission: any) => ({
          // Map API response to frontend Submission interface
          id: submission.id,
          userId: submission.userId,
          content: submission.content,
          type: submission.type as 'highlight' | 'lowlight',
          month: submission.month || new Date().getMonth() + 1,
          year: submission.year || new Date().getFullYear(),
          createdAt: new Date(submission.createdAt),
          updatedAt: new Date(submission.updatedAt),
          workflowState: submission.workflowState as WorkflowState,
          
          // Version control fields
          currentVersion: submission.currentVersion || '1.0',
          originalVersion: submission.originalVersion || '1.0',
          isApproved: submission.isApproved || false,
          isIncludedInReport: submission.isIncludedInReport || false,
          
          // Penholder-specific fields
          penholderAssignedTo: submission.assignedToPenholder,
          penholderAssignedAt: submission.penholderAssignedAt,
          penholderReviewDeadline: submission.reviewDeadline,
          penholderFeedback: submission.penholderFeedback,
          penholderEditedAt: submission.penholderEditedAt,
          
          // Additional metadata from API response
          isOverdue: submission.isOverdue || false,
          daysSinceSubmission: submission.daysSinceSubmission || 0
        }));
      }
      
      return [];
    } catch (error) {
      console.error('Error getting assigned submissions:', error);
      
      // Handle 404 (API Gateway endpoint not configured)
      if (error instanceof Error && (error.message.includes('404') || error.message.includes('403'))) {
        console.warn('API Gateway penholder endpoints not configured. Returning empty submissions list.');
        return [];
      }
      
      throw new Error('Failed to get assigned submissions');
    }
  }

  /**
   * Fallback method to check if user is a penholder (when API Gateway is not configured)
   */
  private async getFallbackAssignment(): Promise<PenholderAssignment | null> {
    // For now, return null to indicate no penholder assignment
    // This will show the "You are not currently assigned as a penholder" message
    // Once API Gateway is configured, this fallback won't be needed
    return null;
  }

  /**
   * Assign a penholder for a specific month
   */
  async assignPenholder(
    userId: string, 
    userName: string, 
    month: number, 
    year: number
  ): Promise<PenholderAssignment> {
    try {
      const payload = {
        userId,
        userName,
        month,
        year
      };

      const response = await this.makeRequest('/api/admin/penholder/assign', 'POST', payload);
      
      if (response.success && response.data && response.data.assignment) {
        const assignment = response.data.assignment;
        return {
          id: assignment.id || assignment.assignmentId,
          penholderUserId: assignment.userId,
          penholderUserName: assignment.userName, // Add userName field
          month: assignment.month,
          year: assignment.year,
          assignedAt: assignment.assignedAt,
          isActive: assignment.status === 'active'
        };
      }
      
      throw new Error('Invalid response format from assign penholder API');
    } catch (error) {
      console.error('Error assigning penholder:', error);
      
      if (error instanceof Error) {
        // Handle specific error cases
        if (error.message.includes('409') || error.message.includes('ASSIGNMENT_EXISTS')) {
          throw new Error('A penholder is already assigned for this month');
        }
        if (error.message.includes('400') || error.message.includes('VALIDATION_ERROR')) {
          throw new Error('Invalid assignment data provided');
        }
        if (error.message.includes('403') || error.message.includes('FORBIDDEN')) {
          throw new Error('Admin access required to assign penholders');
        }
      }
      
      throw new Error('Failed to assign penholder');
    }
  }

  /**
   * Get all penholder assignments for assignment history
   * Note: This is a temporary implementation until we have a proper backend endpoint
   */
  async getAllAssignments(): Promise<PenholderAssignment[]> {
    try {
      // For now, we'll return the current assignment if it exists
      // In the future, this should call a proper /api/admin/penholder/assignments endpoint
      const currentAssignment = await this.getCurrentAssignment();
      
      if (currentAssignment) {
        return [currentAssignment];
      }
      
      return [];
    } catch (error) {
      console.error('Error getting all assignments:', error);
      
      // Handle 404 (API Gateway endpoint not configured)
      if (error instanceof Error && (error.message.includes('404') || error.message.includes('403'))) {
        console.warn('API Gateway admin endpoints not configured. Returning empty assignments list.');
        return [];
      }
      
      throw new Error('Failed to get assignment history');
    }
  }

  /**
   * Get comprehensive assignments view with all months of current year + future assignments
   * Replaces the limited getAllAssignments method
   */
  async getComprehensiveAssignments(year?: number, includeFuture: boolean = true): Promise<AssignmentsResponse> {
    try {
      console.log('🌐 [API DEBUG] Starting getComprehensiveAssignments...');
      console.log('🌐 [API DEBUG] Parameters:', { year, includeFuture });
      
      const queryParams = new URLSearchParams();
      if (year) queryParams.append('year', year.toString());
      if (!includeFuture) queryParams.append('includeFuture', 'false');
      
      const queryString = queryParams.toString();
      const endpoint = `/api/admin/penholder/assignments/comprehensive${queryString ? `?${queryString}` : ''}`;
      
      console.log('🌐 [API DEBUG] API endpoint:', endpoint);
      console.log('🌐 [API DEBUG] Base URL:', this.baseUrl);
      console.log('🌐 [API DEBUG] Full URL:', `${this.baseUrl}${endpoint}`);
      console.log('🌐 [API DEBUG] Auth token available:', !!this.authToken);
      
      const response = await this.makeRequest(endpoint, 'GET');
      
      console.log('🌐 [API DEBUG] Raw response received:', JSON.stringify(response, null, 2));
      
      if (response.success && response.data) {
        console.log('🌐 [API DEBUG] Response data structure:', {
          currentYear: response.data.currentYear,
          assignmentsCount: response.data.assignments?.length,
          futureAssignmentsCount: response.data.futureAssignments?.length,
          totalAssigned: response.data.totalAssigned,
          totalUnassigned: response.data.totalUnassigned
        });
        
        // Log each assignment in the response
        if (response.data.assignments) {
          console.log('🌐 [API DEBUG] Assignments in response:');
          response.data.assignments.forEach((assignment: any, index: number) => {
            console.log(`🌐 [API DEBUG]   ${index + 1}. ${assignment.monthName} ${assignment.year}: ${assignment.status}`, {
              month: assignment.month,
              status: assignment.status,
              penholder: assignment.penholder ? {
                userName: assignment.penholder.userName,
                userId: assignment.penholder.userId,
                assignedAt: assignment.penholder.assignedAt
              } : null
            });
          });
        }
        
        return response.data;
      }
      
      console.error('🌐 [API DEBUG] Invalid response format:', response);
      throw new Error('Invalid response format');
    } catch (error) {
      console.error('🌐 [API DEBUG] Error in getComprehensiveAssignments:', error);
      
      // Handle authentication errors
      if (error instanceof Error && (error.message.includes('401') || error.message.includes('403'))) {
        throw new Error('Admin access required to view comprehensive assignments');
      }
      
      // Handle API not configured
      if (error instanceof Error && error.message.includes('404')) {
        console.warn('🌐 [API DEBUG] Comprehensive assignments API not configured. Falling back to basic assignments.');
        
        // Fallback: create a basic response structure
        const currentYear = year || new Date().getFullYear();
        const monthNames = [
          'January', 'February', 'March', 'April', 'May', 'June',
          'July', 'August', 'September', 'October', 'November', 'December'
        ];
        
        const assignments = monthNames.map((monthName, index) => ({
          year: currentYear,
          month: index + 1,
          monthName,
          status: 'unassigned' as const
        }));
        
        return {
          currentYear,
          assignments,
          futureAssignments: [],
          totalAssigned: 0,
          totalUnassigned: 12
        };
      }
      
      throw new Error('Failed to get comprehensive assignments');
    }
  }

  /* CONTEXT:START
  Wave: 1
  Status: stub
  Feature: Submissions Metrics - Updated API service for new metrics structure
  Dependencies: [Updated PenholderMetrics interface]
  Risk-Level: low
  Performance-Target: <500ms
  Last-Updated: 2025-08-06
  CONTEXT:END */

  /**
   * Get penholder performance metrics
   * Wave 2: Updated to handle new backend response format
   */
  async getMetrics(penholderUserId: string, year: number, month: number): Promise<PenholderMetrics> {
    try {
      console.log(`Fetching metrics for penholder ${penholderUserId}, ${month}/${year}`);
      
      const response = await this.makeRequest(`/api/penholder/metrics?month=${month}&year=${year}`, 'GET');
      console.log('Raw API response:', response);
      
      if (response.success && response.data) {
        // Wave 2: Backend now returns metrics in response.data.metrics
        const metricsData = response.data.metrics || response.data;
        
        console.log('Extracted metrics data:', metricsData);
        
        return {
          penholderUserId: penholderUserId,
          year: response.data.year || year,
          month: response.data.month || month,
          // New Wave 2 metrics from backend
          submitted: metricsData.submitted || 0,
          addedToReport: metricsData.addedToReport || 0,
          pendingReview: metricsData.pendingReview || 0,
          notChosen: metricsData.notChosen || 0,
          // Backward compatibility fields
          totalAssigned: metricsData.totalAssigned || metricsData.submitted || 0,
          totalReviewed: metricsData.totalReviewed || (metricsData.addedToReport || 0) + (metricsData.notChosen || 0),
          totalEdited: metricsData.totalEdited || 0,
          averageReviewTime: metricsData.averageReviewTime || 0
        };
      }
      
      console.warn('No metrics data in response, returning defaults');
      
      // Return default metrics if no data
      return {
        penholderUserId,
        year,
        month,
        submitted: 0,
        addedToReport: 0,
        pendingReview: 0,
        notChosen: 0,
        totalAssigned: 0,
        totalReviewed: 0,
        totalEdited: 0,
        averageReviewTime: 0
      };
    } catch (error) {
      console.error('Error getting penholder metrics:', error);
      
      // Handle 404 (API Gateway endpoint not configured) or 401 (auth required)
      if (error instanceof Error && (error.message.includes('404') || error.message.includes('403') || error.message.includes('401'))) {
        console.warn('API Gateway penholder endpoints not configured or auth required. Returning default metrics.');
        return {
          penholderUserId,
          year,
          month,
          submitted: 0,
          addedToReport: 0,
          pendingReview: 0,
          notChosen: 0,
          totalAssigned: 0,
          totalReviewed: 0,
          totalEdited: 0,
          averageReviewTime: 0
        };
      }
      
      throw new Error('Failed to get penholder metrics');
    }
  }

  /* CONTEXT:START
  Wave: 3
  Status: implemented
  Feature: Submissions Metrics - Wave 3 Complete Integration
  Dependencies: [New Lambda functions, API Gateway endpoints]
  Risk-Level: low
  Performance-Target: <800ms for sectioned submissions, <1.5s for dashboard
  Last-Updated: 2025-08-07
  CONTEXT:END */

  /**
   * Get sectioned submissions for penholder dashboard
   * Wave 3: New endpoint for organized submission display
   */
  async getSectionedSubmissions(penholderUserId: string, year: number, month: number): Promise<SectionedSubmissions> {
    try {
      console.log(`Fetching sectioned submissions for penholder ${penholderUserId}, ${month}/${year}`);
      
      const response = await this.makeRequest(`/api/penholder/sectioned-submissions?month=${month}&year=${year}`, 'GET');
      console.log('Raw sectioned submissions response:', response);
      
      if (response.success && response.data) {
        const sectionedData = response.data.sectionedSubmissions || response.data;
        
        return {
          pendingReview: sectionedData.pendingReview || [],
          addedToReport: sectionedData.addedToReport || [],
          notChosen: sectionedData.notChosen || []
        };
      }
      
      console.warn('No sectioned submissions data in response, returning defaults');
      
      // Return default empty sections
      return {
        pendingReview: [],
        addedToReport: [],
        notChosen: []
      };
    } catch (error) {
      console.error('Error getting sectioned submissions:', error);
      
      // Handle 404 (API Gateway endpoint not configured) or 401 (auth required)
      if (error instanceof Error && (error.message.includes('404') || error.message.includes('403') || error.message.includes('401'))) {
        console.warn('API Gateway sectioned submissions endpoint not configured or auth required. Returning empty sections.');
        return {
          pendingReview: [],
          addedToReport: [],
          notChosen: []
        };
      }
      
      throw new Error('Failed to get sectioned submissions');
    }
  }

  /**
   * Get complete penholder dashboard data
   * Wave 3: Combined endpoint for optimized dashboard loading
   */
  async getDashboardData(penholderUserId: string, year: number, month: number): Promise<{
    metrics: PenholderMetrics;
    sectionedSubmissions: SectionedSubmissions;
    userProfile?: any;
  }> {
    try {
      console.log(`Fetching dashboard data for penholder ${penholderUserId}, ${month}/${year}`);
      
      const response = await this.makeRequest(`/api/penholder/dashboard?month=${month}&year=${year}`, 'GET');
      console.log('Raw dashboard response:', response);
      
      if (response.success && response.data) {
        const dashboardData = response.data;
        
        return {
          metrics: dashboardData.metrics || {
            penholderUserId,
            year,
            month,
            submitted: 0,
            addedToReport: 0,
            pendingReview: 0,
            notChosen: 0,
            totalAssigned: 0,
            totalReviewed: 0,
            totalEdited: 0,
            averageReviewTime: 0
          },
          sectionedSubmissions: dashboardData.sectionedSubmissions || {
            pendingReview: [],
            addedToReport: [],
            notChosen: []
          },
          userProfile: dashboardData.userProfile
        };
      }
      
      console.warn('No dashboard data in response, returning defaults');
      
      // Return default data
      return {
        metrics: {
          penholderUserId,
          year,
          month,
          submitted: 0,
          addedToReport: 0,
          pendingReview: 0,
          notChosen: 0,
          totalAssigned: 0,
          totalReviewed: 0,
          totalEdited: 0,
          averageReviewTime: 0
        },
        sectionedSubmissions: {
          pendingReview: [],
          addedToReport: [],
          notChosen: []
        }
      };
    } catch (error) {
      console.error('Error getting dashboard data:', error);
      
      // Handle 404 (API Gateway endpoint not configured) or 401 (auth required)
      if (error instanceof Error && (error.message.includes('404') || error.message.includes('403') || error.message.includes('401'))) {
        console.warn('API Gateway dashboard endpoint not configured or auth required. Returning default data.');
        return {
          metrics: {
            penholderUserId,
            year,
            month,
            submitted: 0,
            addedToReport: 0,
            pendingReview: 0,
            notChosen: 0,
            totalAssigned: 0,
            totalReviewed: 0,
            totalEdited: 0,
            averageReviewTime: 0
          },
          sectionedSubmissions: {
            pendingReview: [],
            addedToReport: [],
            notChosen: []
          }
        };
      }
      
      throw new Error('Failed to get dashboard data');
    }
  }

  /**
   * Update submission workflow state
   */
  async updateSubmissionState(
    submissionId: string, 
    newState: WorkflowState, 
    feedback?: string
  ): Promise<void> {
    try {
      const payload = {
        workflowState: newState,
        penholderFeedback: feedback || ''
      };

      await this.makeRequest(`/api/submissions/${submissionId}/workflow`, 'PUT', payload);
    } catch (error) {
      console.error('Error updating submission state:', error);
      throw new Error('Failed to update submission state');
    }
  }

  /**
   * Make authenticated HTTP request to API Gateway
   */
  private async makeRequest(endpoint: string, method: string, body?: any): Promise<any> {
    const url = `${this.baseUrl}${endpoint}`;
    console.log('🌐 [HTTP DEBUG] Making API request:', { method, url, hasAuthToken: !!this.authToken });
    
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    // Add authentication header if token is available
    if (this.authToken) {
      headers['Authorization'] = `Bearer ${this.authToken}`;
      console.log('🌐 [HTTP DEBUG] Authorization header added');
    } else {
      console.log('⚠️ [HTTP DEBUG] No auth token available');
    }

    const config: RequestInit = {
      method,
      headers,
      mode: 'cors',
    };

    if (body && (method === 'POST' || method === 'PUT')) {
      config.body = JSON.stringify(body);
      console.log('🌐 [HTTP DEBUG] Request body:', JSON.stringify(body, null, 2));
    }

    try {
      console.log('📡 [HTTP DEBUG] Sending request...');
      const response = await fetch(url, config);
      console.log('📡 [HTTP DEBUG] Response status:', response.status, response.statusText);
      console.log('📡 [HTTP DEBUG] Response headers:', Object.fromEntries(response.headers.entries()));
      
      if (!response.ok) {
        const errorText = await response.text();
        console.log('❌ [HTTP DEBUG] Error response body:', errorText);
        let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        
        try {
          const errorJson = JSON.parse(errorText);
          if (errorJson.error && errorJson.error.message) {
            errorMessage = errorJson.error.message;
          }
        } catch {
          // Use default error message if JSON parsing fails
        }
        
        throw new Error(`${response.status} - ${errorMessage}`);
      }

      const responseText = await response.text();
      console.log('✅ [HTTP DEBUG] Response body (raw):', responseText);
      
      // Handle empty responses
      if (!responseText) {
        console.log('✅ [HTTP DEBUG] Empty response, returning success');
        return { success: true };
      }

      const parsedResponse = JSON.parse(responseText);
      console.log('✅ [HTTP DEBUG] Parsed response:', JSON.stringify(parsedResponse, null, 2));
      return parsedResponse;
    } catch (error) {
      if (error instanceof Error) {
        console.error(`❌ [HTTP DEBUG] API request failed: ${method} ${url}`, error.message);
        throw error;
      }
      
      throw new Error(`Network error: ${method} ${url}`);
    }
  }
}

// Export singleton instance
export const penholderApiService = new PenholderApiService();
